// Módulos de potencia en 3D: relé de 1 canal, puente H L298N y baterías (9 V, portapilas AA y 18650).
import * as THREE from 'three';
import { Builder, M, mat, text } from '../kit.js';
import { RELAY, L298, batteryType, batteryLeadX } from '../../parts/actuators.js';
import {
  P, SILK, PCB_T, pcb, holeRings, silk, pad, traces, maleHeader, jumperCap, screwTerminal, smdLed, labelPlane,
  electrolytic, smd, cable, tinnedTip, sharedTexture, sharedGeo,
} from './actuators-common.js';

// Módulo de relé -------------------------------------------------------------------------------------
const RELAY_ON_ANGLE = -0.2; // giro de la armadura al cerrar NO

/** Mecanismo interno del SRD-05VDC: bobina, culata, armadura con el contacto móvil y contactos fijos. */
function relayMechanism(b, rx, yb) {
  const copper = mat('#b8642a', { roughness: 0.35, metalness: 0.7 });
  const bobbin = mat('#ece7da', { roughness: 0.6 });
  const iron = mat('#8f959c', { roughness: 0.35, metalness: 0.85 });
  const brass = mat('#e2b955', { roughness: 0.28, metalness: 0.9 });
  const silver = mat('#e3e5e8', { roughness: 0.2, metalness: 1 });
  // Bobina horizontal con sus carretes
  b.cylX(4.2, 8.6, copper, rx - 3.6, yb + 6, 0, 24);
  b.cylX(5.2, 0.7, bobbin, rx - 8.2, yb + 6, 0, 24);
  b.cylX(5.2, 0.7, bobbin, rx + 1.0, yb + 6, 0, 24);
  // Núcleo y pieza polar que sube hasta la armadura
  b.cylX(1.9, 1.4, iron, rx + 1.9, yb + 6, 0, 16);
  b.box(1.4, 4.4, 4.2, iron, rx + 2.0, yb + 5.2, 0);
  // Culata en L (atrás y arriba de la bobina) y lámina de COM hacia la base
  b.box(0.8, 11.4, 5.6, iron, rx - 9.0, 0 + yb, 0);
  b.box(7.2, 0.8, 5.6, iron, rx - 5.6, yb + 10.6, 0);
  b.box(0.6, 3.0, 2.4, brass, rx - 9.2, yb - 1.4, -3.4);
  // Contactos fijos: NC arriba y NO abajo, cada uno con su poste hasta la base
  const hy = yb + 11.4;
  const tipX = rx + 7.8;
  b.box(2.4, 0.4, 2.6, brass, tipX + 0.5, hy + 0.75, 0);
  b.cyl(0.85, 0.85, 0.45, silver, tipX, hy + 0.3, 0, 14);
  b.box(0.5, hy + 1.15 - yb, 2.0, brass, tipX + 1.2, yb, 1.6);
  b.box(2.4, 0.4, 2.6, brass, tipX + 0.5, hy - 3.9, 0);
  b.cyl(0.85, 0.85, 0.45, silver, tipX, hy - 3.45, 0, 14);
  b.box(0.5, hy - 3.5 - yb, 2.0, brass, tipX + 1.2, yb, -1.6);
  return { hx: rx - 2, hy, iron, brass, silver };
}

export function buildRelayModule(def, props) {
  const { W, D, PCB_Y, TOP, HEADER_X, TERM_X, BODY_X: RX } = RELAY;
  const b = new Builder();
  const holes = [[-21.6, -10.4, 1.5], [-21.6, 10.4, 1.5], [21.8, -10.8, 1.5], [21.8, 10.8, 1.5]];
  const high = props.trigger === 'high';
  pcb(b.root, {
    key: 'relay-pcb', w: W, d: D, y0: PCB_Y, color: '#173f8c', edge: '#11306b', holes,
    draw: (g, X, Y) => {
      traces(g, 'rgba(10,30,80,0.65)', [
        [[X(HEADER_X), Y(-P)], [X(-16), Y(-6)], [X(-6), Y(-6)], [X(-4), Y(-4.5)]],
        [[X(HEADER_X), Y(P)], [X(-15), Y(4)], [X(-12.5), Y(4)]],
        [[X(RX - 8), Y(6.5)], [X(RX + 9), Y(6.5)], [X(TERM_X - 2), Y(0)]],
        [[X(RX + 9), Y(-5)], [X(TERM_X - 2), Y(-5)]],
        [[X(RX + 9), Y(5)], [X(TERM_X - 2), Y(5)]],
      ], 0.6);
      for (let i = -1; i <= 1; i++) pad(g, X(HEADER_X), Y(i * P), 0.95, i === -1);
      silk(g, 'VCC', X(-20.6), Y(-P), { size: 1.05, align: 'left' });
      silk(g, 'GND', X(-20.6), Y(0), { size: 1.05, align: 'left' });
      silk(g, 'IN', X(-20.6), Y(P), { size: 1.05, align: 'left' });
      silk(g, 'PWR', X(-17.6), Y(-7.8), { size: 0.85 });
      silk(g, 'IN', X(-13.2), Y(-7.8), { size: 0.85 });
      silk(g, 'H', X(-15.5), Y(10.6), { size: 1.0 });
      silk(g, 'L', X(-10.4), Y(10.6), { size: 1.0 });
      silk(g, 'NO', X(16.3), Y(-5), { size: 1.0, align: 'right' });
      silk(g, 'COM', X(16.3), Y(0), { size: 1.0, align: 'right' });
      silk(g, 'NC', X(16.3), Y(5), { size: 1.0, align: 'right' });
      silk(g, '1 Relay Module', X(RX), Y(11.8), { size: 1.0, weight: 600 });
      g.strokeStyle = SILK;
      g.lineWidth = 0.18;
      g.strokeRect(X(RX - 9.8), Y(-8.1), 19.6, 16.2);
      g.strokeRect(X(-16.4), Y(5.6), 7.6, 3.0);
    },
  });
  holeRings(b, holes, TOP);
  maleHeader(b, [[HEADER_X, -P], [HEADER_X, 0], [HEADER_X, P]], TOP);
  // Puente H/L: tres pines y el puente según el disparo
  const jz = 7.1;
  const jx = [-15.04, -12.5, -9.96];
  maleHeader(b, jx.map((x) => [x, jz]), TOP);
  if (high) jumperCap(b, [jx[0], jz], [jx[1], jz], TOP);
  else jumperCap(b, [jx[1], jz], [jx[2], jz], TOP);
  // Optoacoplador PC817, transistor, diodo y resistencias
  b.box(4.6, 3.4, 6.4, M.chip, -12.5, TOP + 0.4, -1.4);
  for (const dx of [-1.27, 1.27]) for (const s of [-1, 1]) b.box(0.5, 1.2, 0.3, M.tin, -12.5 + dx, TOP, -1.4 + s * 3.4);
  b.box(2.9, 1.0, 1.4, M.chip, -7.3, TOP, 2.6);
  for (const dx of [-0.95, 0.95]) b.box(0.4, 0.4, 0.6, M.tin, -7.3 + dx, TOP, 3.5);
  b.box(4.3, 2.0, 2.6, M.chip, -7.3, TOP, -4.6);
  b.box(0.7, 2.02, 2.62, mat('#9aa0a6', { roughness: 0.5 }), -5.6, TOP, -4.6);
  smd(b, -7.3, TOP, 6.0);
  smd(b, -16.2, TOP, -4.6, 'r', Math.PI / 2);
  smd(b, -16.2, TOP, 3.8, 'r', Math.PI / 2);
  // Relé: base negra, mecanismo y caja azul translúcida
  b.box(19, 1.6, 15.5, mat('#121315', { roughness: 0.6 }), RX, TOP, 0);
  const mech = relayMechanism(b, RX, TOP + 1.6);
  const shell = new THREE.MeshPhysicalMaterial({ color: '#1f5fd2', transparent: true, opacity: 0.66, roughness: 0.22, clearcoat: 0.7, clearcoatRoughness: 0.25, depthWrite: false });
  b.rbox(19, 13.8, 15.5, 0.7, shell, RX, TOP + 1.6, 0);
  screwTerminal(b, TERM_X, 0, 3, TOP, '+x', '#2b8a4b');
  b.build();

  // Armadura con el contacto móvil (gira alrededor de la bisagra)
  const arm = new THREE.Group();
  arm.position.set(mech.hx, mech.hy, 0);
  const plate = new THREE.Mesh(new THREE.BoxGeometry(5, 0.8, 5.6), mech.iron);
  plate.position.set(2.5, -0.4, 0);
  const spring = new THREE.Mesh(new THREE.BoxGeometry(5.8, 0.3, 2.6), mech.brass);
  spring.position.set(7.4, -0.4, 0);
  const tipTop = new THREE.Mesh(new THREE.CylinderGeometry(0.85, 0.85, 0.5, 14), mech.silver);
  tipTop.position.set(9.8, 0, 0);
  const tipBottom = tipTop.clone();
  tipBottom.position.set(9.8, -0.8, 0);
  arm.add(plate, spring, tipTop, tipBottom);
  b.root.add(arm);

  // Rótulo de la tapa
  const top = labelPlane(18.6, 15.1, 'relay-top', (g, w, d) => {
    const c = '#eef3ff';
    text(g, 'SONGLE', w / 2 + 2.6, 2.4, { size: 2.3, color: c, weight: 800 });
    text(g, 'SRD-05VDC-SL-C', w / 2 + 1.6, 5.4, { size: 1.55, color: c, weight: 700 });
    text(g, '10A 250VAC  10A 125VAC', w / 2 + 1.6, 8.0, { size: 1.05, color: c, weight: 600 });
    text(g, '10A 30VDC  10A 28VDC', w / 2 + 1.6, 9.8, { size: 1.05, color: c, weight: 600 });
    text(g, 'CQC  ⓒ  CE', w / 2 + 1.6, 12.6, { size: 1.0, color: c, weight: 600 });
    // Símbolo: bobina y contacto conmutado
    g.strokeStyle = c;
    g.lineWidth = 0.22;
    g.strokeRect(0.9, 1.6, 2.2, 3.4);
    g.beginPath();
    g.moveTo(0.9, 5.0);
    g.lineTo(3.1, 1.6);
    g.moveTo(2.0, 1.6);
    g.lineTo(2.0, 0.8);
    g.moveTo(2.0, 5.0);
    g.lineTo(2.0, 6.0);
    g.stroke();
  });
  top.rotation.x = -Math.PI / 2;
  top.position.set(RX, TOP + 15.42, 0);
  top.renderOrder = 2;
  b.root.add(top);

  const pwr = smdLed(b.root, -17.6, TOP, -9.6, '#ff3324');
  const sig = smdLed(b.root, -13.2, TOP, -9.6, '#25e04f');
  let target = 0;
  let shown = 0;
  const view = {
    root: b.root,
    update(v) {
      pwr.set(v.power ? 1 : 0);
      sig.set(v.led ? 1 : 0);
      target = v.closed ? RELAY_ON_ANGLE : 0;
    },
    animate(dt) {
      const d = target - shown;
      if (Math.abs(d) < 1e-4) {
        shown = target;
        arm.rotation.z = shown;
        return false;
      }
      shown += Math.sign(d) * Math.min(Math.abs(d), dt * 6);
      arm.rotation.z = shown;
      return true;
    },
    reset() {
      pwr.set(0);
      sig.set(0);
      target = shown = 0;
      arm.rotation.z = 0;
    },
  };
  view.reset();
  return view;
}

// Puente H L298N --------------------------------------------------------------------------------------
export function buildL298n(def, props) {
  const { W, D, PCB_Y, TOP, HDR_X0, HDR_Z, OUT_Z, PWR_X } = L298;
  const b = new Builder();
  const holes = [[-19, -19, 1.5], [19, -19, 1.5], [-19, 19, 1.5], [19, 19, 1.5]];
  const hdrX = (i) => HDR_X0 + i * P;
  pcb(b.root, {
    key: 'l298-pcb', w: W, d: D, y0: PCB_Y, color: '#b8222b', edge: '#8a1820', holes,
    draw: (g, X, Y) => {
      traces(g, 'rgba(120,10,20,0.6)', [
        [[X(-14), Y(OUT_Z[0])], [X(-9), Y(-3)], [X(-4), Y(-6)]],
        [[X(14), Y(OUT_Z[0])], [X(9), Y(-3)], [X(4), Y(-6)]],
        [[X(PWR_X[0]), Y(14)], [X(-14.5), Y(9)], [X(-7), Y(4)]],
        ...[0, 1, 2, 3, 4, 5].map((i) => [[X(hdrX(i)), Y(HDR_Z)], [X(hdrX(i)), Y(12)], [X(-2 + i * 1.2), Y(-3)]]),
      ], 0.55);
      for (const [x, z] of [[-19, -19], [19, -19], [-19, 19], [19, 19]]) {
        g.strokeStyle = SILK;
        g.lineWidth = 0.2;
        g.beginPath();
        g.arc(X(x), Y(z), 2.6, 0, Math.PI * 2);
        g.stroke();
      }
      silk(g, 'OUT1', X(-17.7), Y(-17.9), { size: 1.05 });
      silk(g, 'OUT2', X(-17.7), Y(-4.3), { size: 1.05 });
      silk(g, 'OUT4', X(17.7), Y(-17.9), { size: 1.05 });
      silk(g, 'OUT3', X(17.7), Y(-4.3), { size: 1.05 });
      silk(g, '+12V', X(PWR_X[0]), Y(12.6), { size: 1.0 });
      silk(g, 'GND', X(PWR_X[1]), Y(12.6), { size: 1.0 });
      silk(g, '+5V', X(PWR_X[2]), Y(12.6), { size: 1.0 });
      ['ENA', 'IN1', 'IN2', 'IN3', 'IN4', 'ENB'].forEach((n, i) => silk(g, n, X(hdrX(i)), Y(13.2), { size: 0.85, rotate: -Math.PI / 2 }));
      silk(g, '5V-EN', X(-12), Y(7.2), { size: 0.85 });
      silk(g, 'L298N', X(0.6), Y(1.4), { size: 1.4, weight: 800 });
      silk(g, 'PWR', X(2.2), Y(9.6), { size: 0.8 });
      for (let i = 0; i < 6; i++) pad(g, X(hdrX(i)), Y(HDR_Z), 0.95, i === 0);
    },
  });
  holeRings(b, holes, TOP);

  // Disipador negro con aletas y el L298N (Multiwatt15) atornillado al frente
  const sink = mat('#1b1c20', { roughness: 0.42, metalness: 0.55 });
  b.box(23, 24.8, 3, sink, 0, TOP, -10.5);
  for (let i = 0; i < 8; i++) b.box(1.2, 24.8, 9, sink, -10.5 + i * 3, TOP, -16.5);
  b.box(19.6, 4.6, 1.2, M.tin, 0, TOP + 19, -8.4);
  b.cylZ(1.7, 0.7, M.steel, 0, TOP + 21.4, -7.45, 16);
  b.box(19.6, 10, 4.5, M.chip, 0, TOP + 9, -6.75);
  for (let i = 0; i < 15; i++) b.box(0.5, 9, 0.4, M.tin, -8.89 + i * 1.27, TOP, i % 2 ? -7.4 : -5.6);
  const chipFace = labelPlane(19.6, 10, 'l298-chip', (g, w, d) => {
    g.fillStyle = '#18181b';
    g.fillRect(0, 0, w, d);
    text(g, 'ST', 3.4, 3.0, { size: 2.0, color: '#9b9da2', weight: 800 });
    text(g, 'L298N', w / 2 + 1.5, 4.2, { size: 2.2, color: '#a9abb0', weight: 700 });
    text(g, 'GK 2V1  MLT', w / 2 + 1.5, 7.2, { size: 1.2, color: '#8d8f94', weight: 500 });
  }, { transparent: false });
  chipFace.position.set(0, TOP + 14, -4.48);
  b.root.add(chipFace);

  // Borneras azules
  screwTerminal(b, -L298.W / 2 + 3.8, (OUT_Z[0] + OUT_Z[1]) / 2, 2, TOP, '-x', '#1f5ac2');
  screwTerminal(b, L298.W / 2 - 3.8, (OUT_Z[0] + OUT_Z[1]) / 2, 2, TOP, '+x', '#1f5ac2');
  screwTerminal(b, PWR_X[1], L298.D / 2 - 3.8, 3, TOP, '+z', '#1f5ac2');

  // Conector de control y puentes
  maleHeader(b, [0, 1, 2, 3, 4, 5].map((i) => [hdrX(i), HDR_Z]), TOP);
  const jz = HDR_Z - P;
  maleHeader(b, [[hdrX(0), jz], [hdrX(5), jz]], TOP);
  if (props.ena) jumperCap(b, [hdrX(0), jz], [hdrX(0), HDR_Z], TOP);
  if (props.enb) jumperCap(b, [hdrX(5), jz], [hdrX(5), HDR_Z], TOP);
  const rj = [[-13.27, 9.4], [-10.73, 9.4]];
  maleHeader(b, rj, TOP);
  if (props.reg) jumperCap(b, rj[0], rj[1], TOP);

  // Regulador 78M05, capacitores, diodos de rueda libre y componentes SMD
  b.box(6.6, 2.3, 6.1, M.chip, -16.6, TOP, 4.2);
  b.box(5.4, 0.5, 1.6, M.tin, -16.6, TOP, 0.6);
  for (const dx of [-2.3, 2.3]) b.box(0.7, 0.5, 1.4, M.tin, -16.6 + dx, TOP, 7.9);
  electrolytic(b.root, -6.4, TOP, 6.6, 3.2, 8.5, { color: '#20232b' });
  electrolytic(b.root, 7.8, TOP, 6.6, 3.2, 8.5, { color: '#20232b' });
  const diode = mat('#18181a', { roughness: 0.5 });
  const band = mat('#a9adb3', { roughness: 0.5 });
  for (const x of [-18.4, -15.4, -12.4, -9.4, 9.4, 12.4, 15.4, 18.4]) {
    b.box(2.6, 2.0, 4.3, diode, x, TOP, -2.0);
    b.box(2.62, 2.02, 0.6, band, x, TOP, -0.25);
  }
  smd(b, 0.6, TOP, 4.2, 'c');
  smd(b, 0.6, TOP, 6.6, 'r');
  smd(b, 3.2, TOP, 11.4, 'r', Math.PI / 2);
  smd(b, -2.0, TOP, 15.4, 'r');
  b.build();

  // Etiqueta del regulador
  const reg = labelPlane(6.2, 5.6, 'l298-78m05', (g, w, d) => {
    g.fillStyle = '#1a1a1d';
    g.fillRect(0, 0, w, d);
    text(g, '78M05', w / 2, d / 2, { size: 1.3, color: '#a2a4a8', weight: 600 });
  }, { transparent: false });
  reg.rotation.x = -Math.PI / 2;
  reg.position.set(-16.6, TOP + 2.32, 4.4);
  b.root.add(reg);

  const led = smdLed(b.root, 2.2, TOP, 11.4, '#ff3324');
  const view = {
    root: b.root,
    update(v) {
      led.set(v.power ? 1 : 0);
    },
    reset() {
      led.set(0);
    },
  };
  view.reset();
  return view;
}

// Batería y portapilas ------------------------------------------------------------------------------------
const RED = '#d23a2e';
const BLACK = '#1e1f22';

/** Forro de una pila (cilindro acostado sobre X): u alrededor, v a lo largo; el + queda en v = 1. */
function cellTexture(key, { base, top, band, label, sub }) {
  return sharedTexture(key, 64, 64, (g) => {
    g.fillStyle = base;
    g.fillRect(0, 0, 64, 64);
    g.fillStyle = top;
    g.fillRect(0, 0, 64, 22);
    if (band) {
      g.fillStyle = band;
      g.fillRect(0, 21, 64, 2.5);
    }
    // Texto a lo largo de la pila, centrado en u = 0.75 (la cara de arriba al acostarla)
    g.save();
    g.translate(48, 32);
    g.rotate(-Math.PI / 2);
    text(g, label, 4, -2.2, { size: 6.2, color: '#f4f1ea', weight: 800 });
    if (sub) text(g, sub, 4, 4.4, { size: 3.6, color: '#e9e3d6', weight: 700 });
    g.restore();
  }, 8);
}

function cell(root, b, { r, len, x, y, z, flip, tex }) {
  const side = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.38, metalness: 0.25 });
  const ends = mat('#c6c9ce', { roughness: 0.3, metalness: 0.9 });
  const mesh = new THREE.Mesh(sharedGeo(`cell${r},${len}`, () => new THREE.CylinderGeometry(r, r, len, 28)), [side, ends, ends]);
  mesh.rotation.z = flip ? Math.PI / 2 : -Math.PI / 2;
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  root.add(mesh);
  // Botón del positivo
  b.cylX(r * 0.38, 1.4, ends, x + (flip ? -1 : 1) * (len / 2 + 0.6), y, z, 16);
}

function holder(b, w, d, h) {
  const plastic = mat('#1a1b1e', { roughness: 0.6 });
  b.rbox(w, 1.4, d, 0.6, plastic, 0, 0, 0);
  b.box(w, h - 1.4, 1.2, plastic, 0, 1.4, -d / 2 + 0.6);
  b.box(w, h - 1.4, 1.2, plastic, 0, 1.4, d / 2 - 0.6);
  b.box(1.6, h - 1.4, d, plastic, -w / 2 + 0.8, 1.4, 0);
  b.box(1.6, h - 1.4, d, plastic, w / 2 - 0.8, 1.4, 0);
}

function buildAaPack(root, b, t, cells, layers) {
  holder(b, t.w, t.d, t.h);
  const per = cells / layers;
  const tex = cellTexture('cell-aa', { base: '#1f1f22', top: '#c8742a', band: '#e8c46a', label: 'AA 1.5V', sub: 'ALKALINE' });
  for (let l = 0; l < layers; l++) {
    for (let i = 0; i < per; i++) {
      const z = (i - (per - 1) / 2) * 14.7;
      cell(root, b, { r: 7.2, len: 49.5, x: 0, y: 1.4 + 7.2 + l * 14.6, z, flip: (i + l) % 2 === 1, tex });
    }
  }
  // Resortes y placas de contacto en los extremos
  for (let i = 0; i < per; i++) {
    const z = (i - (per - 1) / 2) * 14.7;
    for (let l = 0; l < layers; l++) {
      const y = 1.4 + 7.2 + l * 14.6;
      b.cylX(3.4, 1.6, M.steel, -t.w / 2 + 2.6, y, z, 12);
      b.cylX(3.4, 1.0, M.tin, t.w / 2 - 2.2, y, z, 12);
    }
  }
}

function build9v(root, b, t) {
  const L = t.w - 6;
  const x0 = -t.w / 2 + L / 2;
  const tex = sharedTexture('bat9v-top', 44, 26, (g) => {
    g.fillStyle = '#26282d';
    g.fillRect(0, 0, 44, 26);
    g.fillStyle = '#c99a2e';
    g.fillRect(28, 0, 16, 26);
    g.fillStyle = '#e7c35a';
    g.fillRect(28, 0, 1.2, 26);
    text(g, '9V', 14, 12.5, { size: 10, color: '#f3f0e6', weight: 800 });
    text(g, 'ALKALINE', 14, 20.5, { size: 2.6, color: '#d8d4c8', weight: 700 });
    text(g, '6LR61', 36, 13, { size: 2.6, color: '#2a2316', weight: 800, rotate: -Math.PI / 2 });
  }, 10);
  b.rbox(L, t.h, t.d, 1.4, mat('#2a2c31', { roughness: 0.45, metalness: 0.2 }), x0, 0, 0);
  const top = new THREE.Mesh(new THREE.PlaneGeometry(L - 1.6, t.d - 1.6), new THREE.MeshStandardMaterial({ map: tex, roughness: 0.45 }));
  top.rotation.x = -Math.PI / 2;
  top.position.set(x0, t.h + 0.02, 0);
  root.add(top);
  // Conector de broche (snap) sobre los bornes
  const snap = mat('#141416', { roughness: 0.5 });
  b.rbox(6.2, 12.5, 21, 1.2, snap, t.w / 2 - 3.1, 2.5, 0);
  b.box(1.0, 9, 17, mat('#2b2c30', { roughness: 0.5 }), t.w / 2 - 6.4, 4.2, 0);
  return { x: t.w / 2, y: 8.5 };
}

function build18650(root, b, t) {
  holder(b, t.w, t.d, t.h - 3);
  const tex = cellTexture('cell-18650', { base: '#156e7a', top: '#156e7a', band: '#ecd27a', label: '18650 3.7V', sub: 'Li-ion 2600mAh' });
  cell(root, b, { r: 9.1, len: 64, x: 0, y: 1.4 + 9.1, z: 0, flip: false, tex });
  b.cylX(4.6, 1.6, M.steel, -t.w / 2 + 2.6, 10.5, 0, 12);
  b.cylX(4.6, 1.0, M.tin, t.w / 2 - 2.2, 10.5, 0, 12);
  return { x: t.w / 2, y: 6 };
}

export function buildBattery(def, props) {
  const t = batteryType(props);
  const v = String(props.voltage);
  const b = new Builder();
  let exit;
  if (v === '9') exit = build9v(b.root, b, t);
  else if (v === '3.7') exit = build18650(b.root, b, t);
  else {
    const cells = { 4.5: 3, 6: 4, 12: 8 }[v] || 4;
    buildAaPack(b.root, b, t, cells, cells === 8 ? 2 : 1);
    exit = { x: t.w / 2, y: 5 };
  }
  // Cables rojo (+) y negro (−) con las puntas estañadas
  const tip = batteryLeadX(t);
  for (const [z, color] of [[-P / 2, RED], [P / 2, BLACK]]) {
    const z0 = z * 1.6;
    cable(b, [[exit.x - 1.5, exit.y, z0], [exit.x + 3, exit.y - 0.5, z0], [exit.x + 7, 2.2, z * 1.2], [tip - 6, 1.0, z], [tip - 3, 1.0, z]], 0.6, color, 24);
    tinnedTip(b, tip, 1.0, z, 1, 3.2);
  }
  b.build();
  return { root: b.root };
}
